import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, mediaApi, mediaUrl, type ItemMedia, type MediaKind } from '../api';
import ImageCropper from './ImageCropper';

type Props = {
  itemId: number;
  kind: MediaKind;
};

export default function ItemMediaSection({ itemId, kind }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [pendingFilename, setPendingFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: allMedia = [] } = useQuery({
    queryKey: ['item-media', itemId],
    queryFn: () => mediaApi.list(itemId),
  });
  const media = allMedia.filter((m) => m.kind === kind);

  const uploadMutation = useMutation({
    mutationFn: (args: { blob: Blob; filename: string }) =>
      mediaApi.upload('items', itemId, args.blob, kind, args.filename),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-media', itemId] });
      qc.invalidateQueries({ queryKey: ['storage-usage'] });
      setError(null);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? t([`error.${err.message}`, 'error.UNKNOWN']) : t('error.UNKNOWN'),
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => mediaApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-media', itemId] });
      qc.invalidateQueries({ queryKey: ['storage-usage'] });
    },
  });

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    // PDF-kvitteringer uploades direkte uden crop
    if (file.type === 'application/pdf') {
      uploadMutation.mutate({ blob: file, filename: file.name });
      e.target.value = '';
      return;
    }

    // Billeder gennem crop-UI
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setPendingFilename(file.name);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function handleCropComplete(blob: Blob) {
    uploadMutation.mutate({ blob, filename: pendingFilename ?? 'upload.jpg' });
    setCropSrc(null);
    setPendingFilename(null);
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 uppercase">
          {kind === 'photo' ? t('media.photos') : t('media.receipts')}
        </h3>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="text-sm text-slate-600 hover:text-slate-900 hover:underline disabled:opacity-50"
        >
          {uploadMutation.isPending ? t('common.working') : t('media.add')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={kind === 'receipt' ? 'image/*,application/pdf' : 'image/*'}
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {error && <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          aspect={undefined}
          onCancel={() => {
            setCropSrc(null);
            setPendingFilename(null);
          }}
          onCrop={handleCropComplete}
        />
      )}

      {media.length === 0 && !cropSrc ? (
        <p className="text-sm text-slate-500">
          {kind === 'photo' ? t('media.photosEmpty') : t('media.receiptsEmpty')}
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {media.map((m) => (
            <MediaTile key={m.id} media={m} onDelete={() => removeMutation.mutate(m.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function MediaTile({ media, onDelete }: { media: ItemMedia; onDelete: () => void }) {
  const { t } = useTranslation();
  const isPdf = media.mimeType === 'application/pdf';
  const previewUrl = mediaUrl(media, { thumbnail: !!media.thumbnailKey });
  const fullUrl = mediaUrl(media);

  return (
    <div className="relative group bg-slate-100 rounded overflow-hidden aspect-square">
      {isPdf ? (
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center justify-center h-full text-slate-700 hover:bg-slate-200"
        >
          <span className="text-3xl">📄</span>
          <span className="text-xs mt-1 px-2 truncate max-w-full">
            {media.originalFilename ?? 'PDF'}
          </span>
        </a>
      ) : (
        <a href={fullUrl} target="_blank" rel="noopener noreferrer">
          <img src={previewUrl} alt="" className="w-full h-full object-cover" />
        </a>
      )}
      <button
        type="button"
        onClick={() => {
          if (confirm(t('media.confirmDelete'))) onDelete();
        }}
        className="absolute top-1 right-1 bg-white/90 hover:bg-white text-red-600 rounded-full w-7 h-7 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity shadow"
        title={t('common.delete')}
      >
        ×
      </button>
    </div>
  );
}
